import React, { useEffect, useMemo, useState } from 'react';
import { useMutation } from 'react-query';
import { toast } from 'react-toastify';
import { FormGroup, FormLabel } from 'react-bootstrap';
import {
  DataVendor,
  DataProduct,
  DjangoListResponse,
  GenericAssociation,
  DrmEntityBackend,
} from 'v2/models';
import restFactory from 'v2/utils/restFactory';
import { searchAssociationsApiCallData } from 'v2/utils/apiUtils';
import ConfirmationModal from 'v2/common/ConfirmationModal/ConfirmationModal';
import FilterDropdown from 'v2/form/CustomSelectors/FilterDropdown';
import { GeneralSelect } from 'v2/form/CustomSelectors/GeneralSelect';
import styles from 'v2/common/DataVendorModals/modal.module.scss';
import { DRMEntity } from './GenericAssociationTab';
import { useTranslation } from 'react-i18next';
import { TranslationKeys, PostProcessKeys } from 'translations';

type possibleRelationTables = 'DataVendor' | 'DataProduct' | 'Contact';

const apiCalls: searchAssociationsApiCallData<possibleRelationTables> = {
  DataVendor: {
    endpoint: 'data_vendor',
    searchField: 'name__icontains',
    fields: { size: 3 },
  },
  DataProduct: {
    endpoint: 'data_product',
    searchField: 'name',
    fields: { size: 3 },
  },
  Contact: {
    endpoint: 'contact',
    searchField: 'search',
    fields: { size: 3 },
  },
};

interface CreateAssociationModalProps {
  show: boolean;
  entity: DRMEntity;
  onClose: (refresh?: boolean) => void;
  defaultAssociationTable?: possibleRelationTables;
}

const CreateAssociationModal = ({
  show,
  entity,
  onClose,
  defaultAssociationTable = 'DataVendor',
}: CreateAssociationModalProps) => {
  const { t } = useTranslation();
  const [associationTable, setAssociationTable] =
    useState<possibleRelationTables>(defaultAssociationTable);
  const [association, setAssociation] = useState<GenericAssociation>(
    {} as GenericAssociation
  );

  useEffect(() => {
    setAssociation({} as GenericAssociation);
  }, [associationTable, show]);

  const searchCall = useMemo(
    () => async (searchText: string) =>
      (
        await restFactory.get<DjangoListResponse<DataProduct | DataVendor>>(
          `/dj/api/v1/${apiCalls[associationTable].endpoint}/`,
          {
            ...apiCalls[associationTable].fields,
            [apiCalls[associationTable].searchField]: searchText,
          }
        )
      )?.results.map((result) => ({
        name: result.name,
        right_object_id: result.id,
        right_content_type: associationTable,
      })) || [],
    [associationTable]
  );

  const { mutate: createAssociationMutation } = useMutation(
    async () =>
      await restFactory.post(
        `/dj/api/v1/${entity.type}/${entity.id}/associations/`,
        {
          ...association,
        }
      ),
    {
      onSuccess: () => {
        toast.success('Successfully created association');
        onClose(true);
      },
      onError: () => {
        toast.error('Failed to create association');
      },
    }
  );

  const loadOptions = async (
    inputValue: string,
    callback: (arg: { value: Record<string, unknown>; label: string }[]) => void
  ) => {
    const results = (await searchCall(inputValue)).map((result) => ({
      label: result.name,
      value: result,
    }));
    callback(results);
  };

  const groupLabel: { [key in possibleRelationTables]?: string } = {
    DataVendor: t(TranslationKeys.company_one, {
      postProcess: PostProcessKeys.TitleCase,
    }),
    ...(entity.type === DrmEntityBackend.CONTACT && {
      DataProduct: t(TranslationKeys.dataProduct_one, {
        postProcess: PostProcessKeys.TitleCase,
      }),
    }),
    ...(entity.type === DrmEntityBackend.DATASET && {
      Contact: t(TranslationKeys.contact_one, {
        postProcess: PostProcessKeys.TitleCase,
      }),
    }),
  };

  return (
    <ConfirmationModal
      title="Add an Association"
      centered={true}
      animation={true}
      show={show}
      onCancel={() => onClose()}
      okBtnText="Save"
      onOk={async () => await createAssociationMutation()}
      okBtnDisabled={association.right_object_id === undefined}
      className={styles.viewModals}
    >
      <>
        <FormGroup>
          <FormLabel>Association Type</FormLabel>
          <GeneralSelect
            options={Object.entries(groupLabel).map(([key, value]) => ({
              label: value,
              value: key,
            }))}
            selectedOption={{
              value: associationTable,
              label: groupLabel[associationTable] || '',
            }}
            handleChange={(value) =>
              setAssociationTable(
                (value?.value as possibleRelationTables) || associationTable
              )
            }
            placeHolder="Select"
            customStyles={{
              control: {
                width: '100%',
                borderColor: '#ced4da',
              },
              dropdownIndicator: {
                right: '10px',
                justifyContent: 'flex-end',
              },
            }}
          />
        </FormGroup>
        <FormGroup data-cy={`${entity.type}-associations:association-select`}>
          <FormLabel>Associated {groupLabel[associationTable]}</FormLabel>
          <FilterDropdown
            key={JSON.stringify(associationTable)}
            loadOptions={loadOptions}
            defaultValue={null}
            defaultOptions={false}
            onChange={(value) => setAssociation(value || {})}
            placeHolder="Select"
          />
        </FormGroup>
      </>
    </ConfirmationModal>
  );
};

export default CreateAssociationModal;
